import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { resetPassword } from 'aws-amplify/auth';
import { Field, FieldProps, Form, Formik } from 'formik';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IStepFormProps as Props } from 'interfaces/auth';

import { BackButton } from 'components/_basic/BackButton/BackButton';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { FormField } from 'components/_basic/Form/FormField';
import { useAuthModalsContext } from 'components/AuthModals';
import { useResetPasswordContext } from 'components/ResetPassword/ResetPasswordContextProvider/ResetPasswordContextProvider';

import ToastService from 'helpers/ToastService';
import { resetPasswordEmailSchema } from 'helpers/validations/signFormsValidation';

import styles from '../../ResetPassword.module.scss';

interface IEnterEmailStepValues {
  email: string;
}

export const EnterEmailStep = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { nextStep } = useResetPasswordContext();
  const { resetFormData, setResetFormData } = useAuthModalsContext();

  const { track, isPending } = usePromiseStatus();

  const onSubmit = async ({ email }: IEnterEmailStepValues): Promise<void> => {
    try {
      await track(resetPassword({ username: email, options: { autoSignIn: true } }));
      nextStep();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }

      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading image:', error);
      }
    }
  };

  return (
    <Box>
      <Grid className={styles.formHeaderWrapper} container>
        <Grid item xs={11}>
          <BackButton onClick={onClose} />
        </Grid>
        <Grid item xs={1} className={styles.closeButton}>
          <CloseButton onClick={onClose} />
        </Grid>
      </Grid>
      <Typography className={styles.formTitle} variant="h1" sx={{ marginBottom: '24px' }}>
        {t('resetPassword')}
      </Typography>
      <Typography variant="subtitle1" sx={{ marginBottom: '24px' }}>
        {t('resetPasswordNotification')}
      </Typography>
      <Formik
        initialValues={{ email: resetFormData.email }}
        onSubmit={onSubmit}
        validationSchema={resetPasswordEmailSchema}
      >
        {({ isValid, setFieldValue }) => (
          <Form>
            <Field name="email">
              {({ field, meta }: FieldProps<string>) => (
                <FormField
                  id={'login.email'}
                  label={t('Email')}
                  error={meta.touched && meta.error}
                  sx={{ marginBottom: '24px' }}
                >
                  <TextField
                    {...field}
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('email', value);
                      setResetFormData((prevState) => ({ ...prevState, email: value }));
                    }}
                    placeholder={t('enterYourEmail')}
                  />
                </FormField>
              )}
            </Field>
            <Button fullWidth variant="contained" type="submit" disabled={Boolean(!isValid) || isPending}>
              {t('continue')}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
